import { useMemo, type JSX } from 'react';

import { PriceSimulationHeatingTypeDto } from '@eneco/dc-be/client';
import mapScRadioToMap from '@eneco/dc-be/utils/mapScRadioToMap';
import { Button, Heading, RadioGroup, RadioTile, Stack, Stretch, Text } from '@eneco/sparky';
import { Electricity, Gas, Heatpump } from '@eneco/sparky/illustrations';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { HEATING_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

const getHeatingImage = (option: PriceSimulationHeatingTypeDto) => {
  switch (option) {
    case 'ElectricHeating':
      return <Electricity size="small" />;
    case 'GasHeating':
      return <Gas size="small" />;
    case 'HeatPump':
      return <Heatpump size="small" />;
  }
};

export const HeatingCheckStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    control,
    trigger,
    formState: { errors },
    getValues,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const heatingOptions = HEATING_OPTIONS.map(option => ({
    name: option,
    label: option,
  }));

  const sitecoreHeatingOptions = mapScRadioToMap(fields.currentHeatingInstallationTypeStep.heatingTypesList);

  const heatingOptionsMap = useMemo(() => {
    const map = new Map<PriceSimulationHeatingTypeDto, { label: string; name: string; image?: JSX.Element }>();
    for (const option of heatingOptions) {
      map.set(option.name, {
        label: sitecoreHeatingOptions.get(option.name) ?? option.name + ' [not in Sitecore]',
        name: option.name,
        image: getHeatingImage(option.name),
      });
    }
    return map;
  }, [sitecoreHeatingOptions, heatingOptions]);

  const onNextClick = async () => {
    const isValid = await trigger('heatingCheck');
    if (isValid) {
      trackEventBE('pbt_calculation_unknown - current situation', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.currentHeatingInstallationTypeStep.title.value}
        </Heading>
        <Text>{fields.currentHeatingInstallationTypeStep.subTitle.value}</Text>
      </Stack>
      <Stretch>
        <div>
          <Controller
            control={control}
            name={'heatingCheck'}
            render={({ field: { onChange, value } }) => {
              return (
                <RadioGroup
                  label={''}
                  name={'heatingCheck'}
                  isFullWidth
                  value={value}
                  onValueChange={onChange}
                  error={
                    errors.heatingCheck?.message
                      ? fields.currentHeatingInstallationTypeStep.heatingTypesList.value.requiredMessage
                      : undefined
                  }>
                  {Array.from(heatingOptionsMap.values()).map(({ name, label, image }) => (
                    <RadioTile key={name} icon={image} value={name}>
                      {label}
                    </RadioTile>
                  ))}
                </RadioGroup>
              );
            }}
          />
        </div>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
